// Project: GalaxyComplete
// Created: 9/24/20 by sammy
// File: TopBar

import { AppBar, Box, ButtonBase, Chip, Divider, IconButton, Link, Stack, SvgIcon, Theme, Toolbar, Tooltip, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { ComponentType, useCallback, useState } from "react";
import { CurrentUserInfo } from "./CurrentUserInfo";
import { IconBaseProps } from "react-icons";
import { MdMenu, MdOutlineSupportAgent } from "react-icons/md";
import { HelpIcon } from "../../common/CommonIcons";
import { useMountEffect } from "../../common/hooks/hookslib";
import { useAppServices } from "../app/services";
import { useAppGlobalState } from "../app/AppGlobalState";
import { HELP_CENTER_URL, useNavigateToExternalLink } from "../help/HelpCommon";
import { useCurrentProject, useCurrentProjectID, useCurrentProjectState } from "../project/CurrentProjectState";
import { ProjectIcon, useNavigateToProjectDetails, useNavigateToProjectListScreen } from "../project/ProjectCommon";
import { renderProjectUserRole } from "../settings/ProjectUsers";
import { CdsSupportStaffView, useNavigateToSupportProjects } from "../support/SupportCommon";
import { CDSLogo, CDSPrivateEditionLogo } from "./LayoutCommon";
import { useRightSideDrawerWidth } from "./MainLayout";
import { useTranslation } from "react-i18next";
import { useAppLocale } from "../i18n/I18nProvider";
import { getAppLocaleConfig } from "../i18n/localeRegistry";
import { usePrivateEdition } from "../privateEdition/PrivateEditionCommon";

// ======================
// TopBar
// ======================

interface TopBarProps {
    isDesktop: boolean;
    isPrivateEdition: boolean;
    isRightSideDrawerOpen: boolean;
}

export const TopBar: React.FC<TopBarProps> = observer(({ ...p }) => {
    const { isDesktop, isRightSideDrawerOpen } = p;
    const theme = useTheme();
    const rightSideDrawerWidth = useRightSideDrawerWidth();
    const { isPrivateEdition, isMultiTenant } = usePrivateEdition();
    const goBackToProjectPage = useNavigateToProjectListScreen();
    const currentProjectId = useCurrentProjectID();
    const goToProjectDetailsPage = useNavigateToProjectDetails(currentProjectId);
    const goToSupportProjects = useNavigateToSupportProjects();
    const { t } = useTranslation();
    const shouldShowProjectList = isMultiTenant || !currentProjectId;
    const onLogoClick = shouldShowProjectList ? goBackToProjectPage : goToProjectDetailsPage;

    const getTopbarStyles = useCallback(() => {
        if (isDesktop) {
            return {
                background: (t: Theme) => t.palette.cirrus.main,
                zIndex: (t: Theme) => t.zIndex.appBar,
                height: (t: Theme) => t.spacing(9),
            };
        } else {
            return {
                background: (t: Theme) => t.palette.cirrus.medium,
                zIndex: (t: Theme) => t.zIndex.appBar,
            };
        }
    }, [isDesktop]);

    const currentProject = useCurrentProject();
    const myProjectRole = useCurrentProjectState((s) => s.myRole);

    const topBarMenu = isDesktop ? (
        <>
            {isPrivateEdition ? (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Link onClick={onLogoClick}>
                        <CDSPrivateEditionLogo
                            imageAttributes={{
                                width: "224px",
                                height: "100%",
                            }}
                        />
                    </Link>
                    {isMultiTenant && (
                        <TopBarIconButton
                            onClick={goBackToProjectPage}
                            icon={ProjectIcon}
                            label={t("layout:navigation.myProjects")}
                            tooltipLabel={t("layout:navigation.myProjects")}
                        />
                    )}
                </Stack>
            ) : (
                <Stack direction={"row"} spacing={1}>
                    <CDSLogo
                        imageAttributes={{
                            width: "224px",
                            height: "100%",
                        }}
                    />
                    <TopBarIconButton
                        onClick={goBackToProjectPage}
                        icon={ProjectIcon}
                        label={t("layout:navigation.myProjects")}
                        tooltipLabel={t("layout:navigation.myProjects")}
                    />
                    <CdsSupportStaffView>
                        <TopBarIconButton
                            color={"red"}
                            onClick={goToSupportProjects}
                            icon={MdOutlineSupportAgent}
                            tooltipLabel={t("layout:navigation.mySupportProjects")}
                            label={t("layout:navigation.supportProjects")}
                        />
                    </CdsSupportStaffView>
                    {!!currentProject && (
                        <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <Stack pr={1} pl={1} direction={"column"} justifyContent={"center"}>
                                <Box height={40}>
                                    <Divider sx={{ borderColor: (t: Theme) => t.palette.cirrus.light }} orientation={"vertical"} />
                                </Box>
                            </Stack>
                            <Stack direction={"row"} alignItems={"center"} spacing={1}>
                                <Typography variant={"subtitle2"} fontWeight={500}>
                                    {currentProject.getInfo().getName()}
                                </Typography>
                                <Chip size={"small"} label={renderProjectUserRole(myProjectRole)} />
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            )}
        </>
    ) : (
        <Box display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
            <Box pr={1}>
                <MobileMenuIcon />
            </Box>
            <CDSLogo
                imageAttributes={{
                    width: "200px",
                    height: "100%",
                }}
            />
            {isMultiTenant && (
                <TopBarIconButton
                    onClick={goBackToProjectPage}
                    icon={ProjectIcon}
                    label={t("layout:navigation.myProjects")}
                    tooltipLabel={t("layout:navigation.myProjects")}
                />
            )}
        </Box>
    );

    return (
        <AppBar
            elevation={0}
            sx={{
                transition: theme.transitions.create(["margin", "width"], {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                ...(isRightSideDrawerOpen && {
                    width: `calc(100% - ${rightSideDrawerWidth}px)`,
                    marginRight: `${rightSideDrawerWidth}px`,
                    transition: theme.transitions.create(["margin", "width"], {
                        easing: theme.transitions.easing.easeOut,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }),
                borderBottom: (t: Theme) => `1.5px solid ${t.palette.cirrus.light}`,
            }}
        >
            <Toolbar sx={getTopbarStyles()}>
                <Box flexGrow={1} alignItems={"center"} display={"flex"}>
                    {topBarMenu}
                </Box>
                <Stack direction={"row"} justifyContent={"flex-end"} spacing={1}>
                    <CurrentUserInfo isDesktop={isDesktop} />
                </Stack>
            </Toolbar>
        </AppBar>
    );
});

// ======================
// CurrentTimeDisplay
// ======================
interface CurrentTimeDisplayProps {}

const CurrentTimeDisplay: React.FC<CurrentTimeDisplayProps> = observer((p) => {
    const [currentTime, setTime] = useState(new Date());
    const dateFnsLocale = getAppLocaleConfig(useAppLocale()).dateFnsLocale;

    useMountEffect(() => {
        let interval = window.setInterval(() => setTime(new Date()), 1000);
        return () => {
            if (interval) {
                window.clearInterval(interval);
            }
        };
    });

    return (
        <Box>
            <Typography>{format(currentTime, "PPPPpppp", { locale: dateFnsLocale })}</Typography>
        </Box>
    );
});

// ======================
// MobileMenuIcon
// ======================

interface MobileMenuIconProps {}

export const MobileMenuIcon: React.FC<MobileMenuIconProps> = observer((props) => {
    const openSideBar = useAppGlobalState((s) => s.openSideBar);
    const { t } = useTranslation();

    return (
        <IconButton onClick={() => openSideBar()} aria-label={t("layout:aria.mobileSideMenu")}>
            <SvgIcon>
                <MdMenu />
            </SvgIcon>
        </IconButton>
    );
});

// ======================
// SupportButton
// ======================

interface SupportButtonProps {}

export const SupportButton: React.FC<SupportButtonProps> = (p) => {
    const nav = useNavigateToExternalLink(HELP_CENTER_URL);
    const { t } = useTranslation();

    return <TopBarIconButton onClick={nav} icon={HelpIcon} label={t("layout:help")} tooltipLabel={t("layout:navigation.helpCenter")} />;
};

// ======================
// TopBarIconButton
// ======================

interface TopBarIconButtonProps {
    onClick: () => void;
    icon: ComponentType<IconBaseProps>;
    label: string;
    tooltipLabel?: string;
    color?: string;
}

export const TopBarIconButton: React.FC<TopBarIconButtonProps> = (p) => {
    const { onClick, label, tooltipLabel, color } = p;

    return (
        <Tooltip title={tooltipLabel || label}>
            <Box>
                <ButtonBase
                    sx={{
                        borderRadius: 1,
                        "&:hover": {
                            backgroundColor: (t: Theme) => t.palette.action.hover,
                        },
                        minWidth: "60px",
                    }}
                    onClick={onClick}
                >
                    <Stack direction={"column"} alignItems={"center"} justifyContent={"center"} p={1}>
                        <p.icon size={20} color={color} />
                        <Typography
                            variant={"caption"}
                            sx={{
                                color: color,
                            }}
                        >
                            {label}
                        </Typography>
                    </Stack>
                </ButtonBase>
            </Box>
        </Tooltip>
    );
};
